import React, { useEffect, useState } from "react";
import { splitHotelDescriptionParagraphs } from "./buildFlightHotelDetailVm";
import { translateHubwayzHotelDescription } from "../../services/hubwayzHotelsApi";
import { translateTextToHebrew } from "../../utils/translateTextToHebrew";

const PREVIEW_PARAGRAPHS = 2;

function hasHebrewScript(text) {
  return /[\u0590-\u05FF]/.test(String(text || ""));
}

function mostlyHebrew(text) {
  const letters = String(text || "").match(/[A-Za-z\u0590-\u05FF]/g) || [];
  if (!letters.length) return false;
  const hebrew = letters.filter((ch) => ch >= "\u0590" && ch <= "\u05FF").length;
  return hebrew / letters.length >= 0.35;
}

function chunksForTranslation(text) {
  const blocks = String(text || "").split(/\n{2,}/);
  const chunks = [];
  for (const block of blocks) {
    let rest = block.trim();
    if (!rest) {
      chunks.push("");
      continue;
    }
    while (rest.length > 900) {
      let cut = rest.lastIndexOf(" ", 900);
      if (cut < 400) cut = 900;
      chunks.push(rest.slice(0, cut).trim());
      rest = rest.slice(cut).trim();
    }
    if (rest) chunks.push(rest);
  }
  return chunks;
}

async function translateDescriptionInBrowser(source) {
  const parts = [];
  for (const chunk of chunksForTranslation(source)) {
    if (!chunk) {
      parts.push("");
      continue;
    }
    if (mostlyHebrew(chunk)) {
      parts.push(chunk);
      continue;
    }
    const translated = await translateTextToHebrew(chunk);
    const text = String(translated || "").trim();
    parts.push(hasHebrewScript(text) ? text : chunk);
  }
  return parts.join("\n\n").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Hotel description under the gallery.
 * Hebrew comes from the server cache. The English supplier text is shown
 * only when that translation is missing.
 */
export default function HubwayzHotelAbout({ description }) {
  const source = String(description || "").trim();
  const sourceHebrew = hasHebrewScript(source);
  const [text, setText] = useState(source);
  const [language, setLanguage] = useState(sourceHebrew ? "he" : "en");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setExpanded(false);
    if (!source) return undefined;
    if (mostlyHebrew(source)) {
      setText(source);
      setLanguage("he");
      return undefined;
    }
    setText(source);
    setLanguage("en");
    translateHubwayzHotelDescription(source)
      .then(async (result) => {
        if (cancelled) return null;
        const translated = String(result?.text || "").trim();
        if (result?.language === "he" && mostlyHebrew(translated)) return translated;
        return translateDescriptionInBrowser(source);
      })
      .then((translated) => {
        if (cancelled) return;
        const text = String(translated || "").trim();
        if (mostlyHebrew(text)) {
          setText(text);
          setLanguage("he");
          return;
        }
        setText(source);
        setLanguage("en");
      })
      .catch(() => {
        if (!cancelled) {
          setText(source);
          setLanguage("en");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [source, sourceHebrew]);

  if (!source) return null;
  const paragraphs = splitHotelDescriptionParagraphs(text);
  if (!paragraphs.length) return null;
  const hidden = paragraphs.length > PREVIEW_PARAGRAPHS;
  const shown = expanded ? paragraphs : paragraphs.slice(0, PREVIEW_PARAGRAPHS);
  const isHebrew = language === "he";

  return (
    <section
      className="hpm-section usm-fh-about"
      id="about-hotel"
      dir="rtl"
      lang="he"
      aria-labelledby="about-hotel-title"
    >
      <div className="hpm-section__head usm-fh-section__static-head">
        <h2 id="about-hotel-title">אודות המלון</h2>
      </div>
      <div className="hpm-section__body">
        <div
          className="usm-fh-about__text"
          dir={isHebrew ? "rtl" : "ltr"}
          lang={isHebrew ? "he" : "en"}
        >
          {shown.map((paragraph, index) => (
            <p key={`${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>
          ))}
        </div>
        {hidden ? (
          <button
            type="button"
            className="usm-fh-about__more"
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded ? "הצג פחות" : "הצג עוד"}
          </button>
        ) : null}
      </div>
    </section>
  );
}
